import { formatRecordDate } from "@taunggyi-plus/design-system";
import { FestivalWorkspace } from "./festival-marketplace";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useAuth, usePagedRecords } from "@taunggyi-plus/auth";
import {
  ActionIcon,
  Card,
  DetailCard,
  ManagementListToolbar,
  PaginatedList,
  ServerPagination,
} from "@taunggyi-plus/design-system";

type EventRecord = {
  id: string;
  title: string;
  description: string;
  venue: string;
  starts_on: string;
  ends_on: string;
  application_deadline: string;
  status: string;
};
type Org = { id: string; name: string; kind: string };
type Participation = {
  id: string;
  event_id: string;
  organization_id: string;
  fee_minor: number;
  status: string;
  note: string;
};
type Fee = { event_id: string; service: string; fee_minor: number };
const services = {
  hotel: "Hotels",
  merchant: "Food providers",
  delivery_fleet: "Delivery providers",
  taxi_group: "Taxi drivers",
};
const money = (minor: number) => `${(minor / 100).toLocaleString()} MMK`;
function EventListWorkspace({ mode = "customer" }: { mode?: "customer" | "provider" | "admin" }) {
  const { client, user } = useAuth();
  const [orgs, setOrgs] = useState<Org[]>([]),
    [fees, setFees] = useState<Fee[]>([]),
    [applications, setApplications] = useState<Participation[]>([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [search, setSearch] = useState(""),
    [status, setStatus] = useState("all"),
    [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<EventRecord | null | undefined>(undefined);
  const records = usePagedRecords<EventRecord>("events", search, status);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const ids = records.items.map((e) => e.id).join(",");
  const refresh = useCallback(async () => {
    if (!client) return;
    const owned =
      mode === "provider" && user
        ? await client.rpc("my_event_organizations")
        : { data: [], error: null };
    if (owned.error) throw new Error(owned.error.message);
    const [fs, os, ps] = await Promise.all([
      ids
        ? client.from("event_service_fees").select("*").in("event_id", ids.split(","))
        : Promise.resolve({ data: [], error: null }),
      mode === "customer"
        ? Promise.resolve({ data: [], error: null })
        : mode === "provider"
          ? Promise.resolve(owned)
          : client.from("organizations").select("id,name,kind"),
      mode !== "provider" || !ids
        ? Promise.resolve({ data: [], error: null })
        : client.from("event_participations").select("*").in("event_id", ids.split(",")),
    ]);
    const error = [fs, os, ps].find((r) => r.error)?.error;
    if (error) throw new Error(error.message);
    setFees((fs.data ?? []) as Fee[]);
    setOrgs((os.data ?? []) as Org[]);
    setApplications((ps.data ?? []) as Participation[]);
  }, [client, user, mode, ids]);
  useEffect(() => {
    const timer = setTimeout(() => void refresh().catch((e) => setMessage(e.message)), 0);
    return () => clearTimeout(timer);
  }, [refresh, setMessage]);
  async function action(name: string, args: Record<string, unknown>) {
    if (!client || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const r = await client.rpc(name, args);
      if (r.error) throw new Error(r.error.message);
      await refresh();
      records.refresh();
      setVersion((v) => v + 1);
      setEditing(undefined);
      setMessage("Saved.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not save. Please retry.");
    } finally {
      setBusy(false);
    }
  }
  function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void action("save_platform_event", {
      target_id: editing?.id ?? null,
      title: f.get("title"),
      description: f.get("description"),
      venue: f.get("venue"),
      starts: f.get("starts"),
      ends: f.get("ends"),
      deadline: f.get("deadline"),
      status: f.get("status"),
      fees: Object.fromEntries(
        Object.keys(services)
          .filter((s) => Number(f.get(s)) > 0)
          .map((s) => [s, Math.round(Number(f.get(s)) * 100)]),
      ),
    });
  }
  const matching = records.items;
  const loading = records.loading;
  return (
    <section className="management-card">
      {message && <p role="status">{message}</p>}
      {loading && <p role="status">Loading events…</p>}
      <div className="events-list-toolbar">
        <ManagementListToolbar
          actions={
            mode === "admin" && (
              <button
                type="button"
                className="toolbar-icon-button"
                aria-label="Create event"
                title="Create event"
                aria-haspopup="dialog"
                onClick={() => setEditing(null)}
              >
                <ActionIcon action="add" />
              </button>
            )
          }
          query={search}
          onQueryChange={setSearch}
          searchLabel="Search events"
          resultCount={records.total}
          filters={[
            ...(mode === "provider"
              ? [
                  {
                    label: "Provider business",
                    value: selectedOrg || "all",
                    onChange: (value: string) => setSelectedOrg(value === "all" ? "" : value),
                    options: [
                      { value: "all", label: "Choose your business" },
                      ...orgs.map((org) => ({ value: org.id, label: org.name })),
                    ],
                  },
                ]
              : []),
            {
              label: "Status",
              value: status,
              onChange: setStatus,
              options: [
                "all",
                ...(mode === "admin" ? ["draft"] : []),
                "published",
                "cancelled",
                "completed",
              ].map((value) => ({ value, label: value === "all" ? "All statuses" : value })),
            },
          ]}
          onClear={() => {
            setSearch("");
            setStatus("all");
          }}
        />

        <button
          className="toolbar-icon-button"
          aria-label="Refresh events"
          title="Refresh events"
          disabled={busy}
          onClick={() => {
            setMessage("");
            records.refresh();
            setVersion((v) => v + 1);
            void refresh().catch((e) => setMessage(e.message));
          }}
        >
          <ActionIcon action="refresh" />
        </button>
      </div>
      {editing !== undefined && (
        <dialog
          ref={(node) => {
            if (node && !node.open) node.showModal();
          }}
          className="customer-order-filter-dialog subscription-popup"
          aria-label={editing ? "Edit event" : "Create event"}
          onCancel={(event) => {
            if (busy) event.preventDefault();
          }}
          onClose={() => setEditing(undefined)}
        >
          <h3>{editing ? "Edit event" : "Create event"}</h3>
          <form className="form-grid" onSubmit={save}>
            <label className="field">
              Title
              <input
                name="title"
                defaultValue={editing?.title}
                placeholder="Taunggyi Tazaungdaing Festival 2026"
                minLength={3}
                maxLength={120}
                required
              />
            </label>
            <label className="field">
              Description
              <textarea
                name="description"
                defaultValue={editing?.description}
                minLength={10}
                maxLength={2000}
                required
              />
            </label>
            <label className="field">
              Venue
              <input
                name="venue"
                defaultValue={editing?.venue}
                minLength={2}
                maxLength={240}
                required
              />
            </label>
            {(
              [
                ["starts", "Start date", "starts_on"],
                ["ends", "End date", "ends_on"],
                ["deadline", "Application deadline", "application_deadline"],
              ] as const
            ).map(([name, label, key]) => (
              <label className="field" key={name}>
                {label}
                <input type="date" name={name} defaultValue={editing?.[key]} required />
              </label>
            ))}
            <label className="field">
              Status
              <select name="status" defaultValue={editing?.status ?? "draft"}>
                {["draft", "published", "cancelled", "completed"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            {Object.entries(services).map(([s, label]) => (
              <label className="field" key={s}>
                {label} fee (MMK)
                <input
                  name={s}
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={
                    editing
                      ? Number(
                          fees.find((f) => f.event_id === editing.id && f.service === s)
                            ?.fee_minor ?? 0,
                        ) / 100
                      : undefined
                  }
                />
              </label>
            ))}
            <p>
              Leave a service fee blank or zero to disable participation for that service. One
              participation fee covers all approved festival promotions. Price changes apply to new
              applications. Existing applications retain their quoted fee.
            </p>
            <button className="button" disabled={busy}>
              Save event
            </button>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setEditing(undefined)}
              disabled={busy}
            >
              Cancel
            </button>
          </form>
        </dialog>
      )}
      <p role="status">{records.error}</p>
      {(mode !== "customer" || matching.length > 0) && (
        <PaginatedList
          pagination="external"
          layout={mode === "customer" ? "cards" : "table"}
          items={matching}
          resetKey={`${search}:${status}`}
          label="Event pages"
          renderItem={(e) =>
            mode === "customer" ? (
              <Card className="customer-event-card" key={e.id}>
                <div className="customer-event-card__date">
                  {formatRecordDate(e.starts_on)} – {formatRecordDate(e.ends_on)}
                </div>
                <h2>{e.title}</h2>
                <p>{e.description}</p>
                <p className="customer-event-card__venue">{e.venue}</p>
                <a className="button" href={`/events/${e.id}`}>
                  Explore festival services
                </a>
              </Card>
            ) : (
              <DetailCard
                key={e.id}
                title={e.title}
                summary={<span className="status-pill">{e.status}</span>}
              >
                <h3>{e.title}</h3>
                <a
                  className="button"
                  href={`${mode === "admin" ? "/more/events" : "/more/provider-events"}/${e.id}`}
                >
                  Manage this event
                </a>
                <span className="status-pill">{e.status}</span>
                <p>{e.description}</p>
                <p>
                  {e.venue} · {formatRecordDate(e.starts_on)} – {formatRecordDate(e.ends_on)}
                </p>
                <p>Apply by {e.application_deadline}</p>
                {
                  <ul>
                    {fees
                      .filter((f) => f.event_id === e.id)
                      .map((f) => (
                        <li key={f.service}>
                          {services[f.service as keyof typeof services]}: {money(f.fee_minor)}
                        </li>
                      ))}
                  </ul>
                }
                {mode === "admin" && (
                  <button className="button" onClick={() => setEditing(e)}>
                    Edit event
                  </button>
                )}
                {mode === "provider" && (
                  <p>
                    Your application:{" "}
                    {applications.find(
                      (p) => p.event_id === e.id && p.organization_id === selectedOrg,
                    )?.status ?? "Not submitted"}
                    . Festival promotions are included in the participation fee.
                  </p>
                )}
                {mode === "provider" &&
                  e.status === "published" &&
                  selectedOrg &&
                  fees.some(
                    (f) =>
                      f.event_id === e.id &&
                      f.service === orgs.find((o) => o.id === selectedOrg)?.kind,
                  ) &&
                  !applications.some(
                    (p) => p.event_id === e.id && p.organization_id === selectedOrg,
                  ) && (
                    <button
                      className="button"
                      disabled={busy}
                      onClick={() =>
                        void action("apply_to_event", { event: e.id, org: selectedOrg })
                      }
                    >
                      Apply to participate ·{" "}
                      {money(
                        fees.find(
                          (f) =>
                            f.event_id === e.id &&
                            f.service === orgs.find((o) => o.id === selectedOrg)?.kind,
                        )?.fee_minor ?? 0,
                      )}
                    </button>
                  )}
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                >
                  {expanded === e.id ? "Hide participants" : "View participants"}
                </button>
                {expanded === e.id && (
                  <EventParticipants key={`${e.id}:${version}`} event={e.id} mode={mode} />
                )}
              </DetailCard>
            )
          }
        />
      )}
      {!loading && !matching.length && (
        <Card className="empty-state">
          <h3>No events found</h3>
          <p>No events match these filters.</p>
        </Card>
      )}
      {records.total > 5 && (
        <ServerPagination
          page={records.page}
          total={records.total}
          onPage={records.setPage}
          loading={records.loading}
        />
      )}
    </section>
  );
}

export function EventParticipants({
  event,
  mode,
}: {
  event: string;
  mode: "customer" | "provider" | "admin";
}) {
  const { client } = useAuth();
  const [search, setSearch] = useState(""),
    [status, setStatus] = useState("all"),
    [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false);
  const records = usePagedRecords<Participation & { name: string; paid: boolean }>(
    mode === "customer" ? "public_participants" : "participants",
    search,
    status,
    event,
  );
  return (
    <section>
      <h4>{mode === "customer" ? "Confirmed participants" : "Participation applications"}</h4>
      <ManagementListToolbar
        query={search}
        onQueryChange={setSearch}
        searchLabel="Search providers"
        resultCount={records.total}
        filters={
          mode === "customer"
            ? []
            : [
                {
                  label: "Status",
                  value: status,
                  onChange: setStatus,
                  options: ["all", "pending", "approved", "rejected", "cancelled"].map((value) => ({
                    value,
                    label: value === "all" ? "All statuses" : value,
                  })),
                },
              ]
        }
        onClear={() => {
          setSearch("");
          setStatus("all");
        }}
      />
      <p role="status">{records.error || message}</p>
      {records.items.map((p) => (
        <Card key={p.id}>
          <strong>{p.name}</strong>
          {mode !== "customer" && (
            <p>
              {money(p.fee_minor)} ·{" "}
              {p.status === "approved"
                ? p.paid
                  ? "Confirmed · paid"
                  : "Approved · awaiting payment"
                : p.status}
            </p>
          )}
          {mode === "admin" && p.status === "approved" && (
            <button
              className="button button--danger"
              disabled={busy}
              onClick={async () => {
                if (!client) return;
                setBusy(true);
                try {
                  const r = await client.rpc("hide_event_partner", { event, participation: p.id });
                  if (r.error) throw Error(r.error.message);
                  records.refresh();
                } catch (e) {
                  setMessage(e instanceof Error ? e.message : "Could not hide partner");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Hide partner from this event
            </button>
          )}
          {mode === "admin" && p.status === "pending" && (
            <form
              className="form-grid"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!client || busy) return;
                const f = new FormData(e.currentTarget);
                setBusy(true);
                try {
                  const r = await client.rpc("review_event_participation", {
                    participation: p.id,
                    decision: f.get("decision"),
                    note: f.get("note"),
                  });
                  if (r.error) throw new Error(r.error.message);
                  records.refresh();
                  setMessage(
                    "Application reviewed. Record payment in Finance after verifying receipt.",
                  );
                } catch (e) {
                  setMessage(e instanceof Error ? e.message : "Could not review.");
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label className="field">
                Decision
                <select name="decision">
                  <option value="approved">Approve and charge</option>
                  <option value="rejected">Reject</option>
                </select>
              </label>
              <label className="field">
                Review note
                <input name="note" maxLength={500} />
              </label>
              <button className="button" disabled={busy}>
                Save decision
              </button>
            </form>
          )}
        </Card>
      ))}
      {mode !== "customer" && <a href="/finance">Fees and receipts</a>}
      <ServerPagination
        page={records.page}
        total={records.total}
        onPage={records.setPage}
        loading={records.loading}
      />
    </section>
  );
}

export function EventsWorkspace({
  mode = "customer",
}: {
  mode?: "customer" | "provider" | "admin";
}) {
  const match = window.location.pathname.match(/\/(?:events|provider-events)\/([^/]+)$/);
  return match ? (
    <FestivalWorkspace key={match[1]} event={decodeURIComponent(match[1]!)} mode={mode} />
  ) : (
    <EventListWorkspace mode={mode} />
  );
}
